import * as React from "react";

import { Form } from "kanopi-components/Form";
import { Modal } from "kanopi-components/Modal";
import { Question } from "../Question";
import { ResponsiveNextButton } from "kw/components/ResponsiveNextButton/ResponsiveNextButton";
import { Rhetoricals } from "../Rhetorical";
import { handleErrorDisplay } from "kw/util/utils";
import { identity } from "kanopi-components/utils";
import { useForm } from "react-weaver";
import { useYupSchema } from "kanopi-components/useYupSchema";

type TQuestionSetProps = {
  questionSet: TQuestionSet;
  disabled?: boolean;
  onSubmit?: (result) => void;
  onError?: TOnFormEventFunc;
  onChange?: TOnFormEventFunc;
  initialValues?: object;
  noNextButton?: boolean;
  nextButtonText?: string;
  prefix?: string;
  // requiring 'key' to prevent bugs when switching to another questionSet between renders
  [key: string]: TODO;
};

const InnerQuestionSet: React.FC<TQuestionSetProps> = ({
  questionSet = {},
  disabled = false,
  onSubmit,
  onError = identity,
  onChange,
  initialValues,
  noNextButton,
  nextButtonText = "Next",
  prefix,
  children,
}) => {
  const questions = (questionSet.questions || []).sort((a, b) => a.order - b.order);
  const [formInteracted, setFormInteracted] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState({});

  function handleFocus() {
    if (!formInteracted) {
      setFormInteracted(true);
    }
  }

  async function doSubmit(values) {
    // This function is a little annoying; it transforms from ID keys to
    // storage path keys. It's only needed because Yup can't handle
    // periods in the field name.
    const result = {};
    for (const [k, v] of Object.entries(values)) {
      const question = questions.find((q) => q.id == k);
      result[question.storagePath] = v;
    }
    await onSubmit?.(result);
  }

  async function handleSubmit() {
    // Handle any form/system errors after submit by displaying a toast.
    try {
      await submit();
    } catch (e) {
      handleErrorDisplay(e);

      if (e.response?.errors) {
        e.response?.errors.forEach((error) => {
          if (error?.details && !!Object.keys(error.details).length) {
            setFieldErrors(error.details);
          }
        });
      }
    }
  }

  const validator = useYupSchema({
    fields: questions,
    formValidators: questionSet.validators,
  });
  const { fieldProps, submit, anyValidating, loading, errors, touched } = useForm({
    initialValues:
      initialValues &&
      Object.keys(initialValues).reduce(
        (carry: Record<string, unknown>, key: string) => {
          const initialValue = initialValues[key];
          const convertedValue =
            initialValue === "False"
              ? false
              : initialValue === "True"
              ? true
              : initialValue;
          return {
            ...carry,
            [key]: convertedValue,
          };
        },
        {}
      ),
    validator,
    onSubmit: doSubmit,
    onChange,
    onError,
  });

  const modal = Modal.useElem();

  return (
    <Form onFocus={handleFocus}>
      {questions.map((q, i) => {
        const rhetoricalsOnTop = q?.metadata?.rhetoricalsOnTop ?? false;
        return (
          <React.Fragment key={q.id}>
            {rhetoricalsOnTop && <Rhetoricals rhetoricals={q.rhetoricals} />}
            <Question
              question={q}
              autoFocus={!i}
              prefix={prefix}
              {...fieldProps[q.id]}
              error={touched && touched[q.id] ? errors[q.id] : fieldErrors[q.id]}
              disabled={loading || fieldProps[q.id].disabled || disabled}
              initialLoad={!formInteracted}
            />
            {!rhetoricalsOnTop && <Rhetoricals rhetoricals={q.rhetoricals} />}
          </React.Fragment>
        );
      })}

      {children ? children({ fieldProps, questions }) : null}
      {!noNextButton && (
        <ResponsiveNextButton
          fixed={!modal}
          narrow={!!modal}
          onClick={handleSubmit}
          disabled={loading || disabled || anyValidating}
          isLoading={loading}
          text={nextButtonText}
        />
      )}
    </Form>
  );
};

export const QuestionSet = React.memo(InnerQuestionSet);
